import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchPlatformOverview, PlatformOverviewParams } from '../services/analyticsService';
import { PlatformOverviewResponse } from '../types/analytics';
import { useTranslation } from 'react-i18next';
import { SocialPlatform } from '../components/icons/SocialIcon';
import { 
  Users, 
  ThumbsUp, 
  FileText, 
  Eye, 
  MousePointer, 
  MessageSquare, 
  Send, 
  BarChart, 
  Percent, 
  Clock, 
  ThumbsDown, 
  Bookmark, 
  User, 
  ExternalLink, 
  Play, 
  Circle, 
  AtSign, 
  DollarSign, 
  Hash, 
  Video,
  Heart
} from 'lucide-react';
import { auth } from '../config/firebase';
import { useEffect } from 'react';

interface DisplayMetric {
  title: string;
  value: number;
  previousValue: number;
  change: number;
  icon: React.ElementType;
  color: string;
}

/**
 * Custom hook to fetch and manage platform-specific analytics data
 * @param params - Parameters for the platform overview request
 * @returns Query result with platform-specific analytics data
 */
export const usePlatformOverview = ({
  platform,
  start,
  end,
  brandId,
  pageId,
  hasPlatformAccount,
  refresh,
  withAnalysis
}: {
  platform: SocialPlatform | null;
  start: string;
  end: string;
  brandId?: string;
  pageId?: string;
  hasPlatformAccount: boolean;
  refresh?: string | null;
  withAnalysis?: boolean;
}) => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  
  // Invalidate cache when brand or platform changes
  useEffect(() => {
    if (platform && brandId) {
      console.log('Invalidating platform-overview cache for:', platform, brandId);
      queryClient.invalidateQueries({ queryKey: ['platform-overview', platform, brandId] });
    }
  }, [platform, brandId, queryClient]);

  return useQuery({
    queryKey: ['platform-overview', platform, start, end, brandId, pageId, refresh, withAnalysis],
    queryFn: async () => {
      if (!platform || !hasPlatformAccount) {
        throw new Error('No platform selected or no account connected');
      }
      
      console.log('Fetching platform data with pageId:', pageId);
      console.log('Query key:', ['platform-overview', platform, start, end, brandId, pageId]);

      return fetchPlatformOverview({
        platform,
        start,
        end,
        brandId,
        pageId,
        applanguage: i18n.language || 'en',
        withAnalysis
      });
    },
    enabled: Boolean(platform && hasPlatformAccount),
    staleTime: 5 * 60 * 1000 // Consider data fresh for 5 minutes
  });
};

/**
 * Convert platform metrics to display metrics format
 * @param data - Platform overview response data
 * @returns Array of display metrics with icon and color information
 */
export const convertToDisplayMetrics = (data: any): DisplayMetric[] => {
  if (!data || !data.metrics) return [];

  const metricsMap = {
    FOLLOWERS: { title: 'Followers', icon: Users, color: 'bg-blue-500' },
    ORGANICFOLLOWERS: { title: 'Organic Followers', icon: Users, color: 'bg-green-500' },
    PAIDFOLLOWERS: { title: 'Paid Followers', icon: DollarSign, color: 'bg-purple-500' },
    POSTS: { title: 'Posts', icon: FileText, color: 'bg-orange-500' },
    IMPRESSIONS: { title: 'Impressions', icon: Eye, color: 'bg-indigo-500' },
    UNIQUE_IMPRESSIONS: { title: 'Unique Impressions', icon: Eye, color: 'bg-cyan-500' },
    CLICKS: { title: 'Clicks', icon: MousePointer, color: 'bg-emerald-500' },
    REACTIONS: { title: 'Reactions', icon: ThumbsUp, color: 'bg-yellow-500' },
    COMMENTS: { title: 'Comments', icon: MessageSquare, color: 'bg-amber-500' },
    REPOSTS: { title: 'Reposts', icon: Send, color: 'bg-red-500' },
    CTR: { title: 'CTR', icon: BarChart, color: 'bg-pink-500' },
    ENGAGEMENT_RATE: { title: 'Engagement Rate', icon: Percent, color: 'bg-rose-500' },
    PAGE_VIEWS: { title: 'Page Views', icon: FileText, color: 'bg-slate-500' },
    UNIQUE_PAGE_VIEWS: { title: 'Unique Page Views', icon: Hash, color: 'bg-gray-500' },
    VIEWS: { title: 'Views', icon: Video, color: 'bg-red-600' },
    LIKES: { title: 'Likes', icon: ThumbsUp, color: 'bg-pink-600' },
    DISLIKES: { title: 'Dislikes', icon: ThumbsDown, color: 'bg-gray-600' },
    ESTIMATEDMINUTESWATCHED: { title: 'Minutes Watched', icon: Clock, color: 'bg-teal-500' },
    AVERAGEVIEWDURATION: { title: 'Avg. View Duration (s)', icon: Clock, color: 'bg-sky-500' }
  };

  return data.metrics
    .filter((metric: any) => metric.type in metricsMap)
    .map((metric: any) => {
      const { title, icon, color } = metricsMap[metric.type as keyof typeof metricsMap];
      const value = parseFloat(metric.total) || 0;
      const previousValue = parseFloat(metric.previousTotal) || 0;
      let change = 0;

      // Handle special case for infinite percentage
      if (metric.variationsPercentage === '∞') {
        change = previousValue === 0 && value > 0 ? 100 : 0;
      } else {
        change = parseFloat(metric.variationsPercentage) || 0;
      }

      return {
        title,
        value,
        previousValue,
        change,
        icon,
        color
      };
    });
}; 